import { Badge } from '@/components/ui';
import { statusDetail, statusLabel, statusTone, type XeroLink } from '@/lib/xero';

export interface XeroStatusBadgeProps {
  link: XeroLink;
  className?: string;
}

/**
 * The one rendering of a Xero sync status, so a table cell and a detail panel
 * can never drift into saying different things about the same record.
 *
 * The label is always present, and that is not decoration: six statuses collapse
 * to four tones — `conflict` and `archived` both read `warning` — so colour
 * alone cannot separate "someone edited this in Xero" from "the Xero record is
 * archived", and those want opposite actions.
 *
 * `statusDetail` rides along as a `title`, because a badge's job in a list is to
 * fit on one line; the sentence saying what to do about it is what `XeroPanel`
 * shows in full. The tooltip is a hint, never the only place a fact lives —
 * it is unreachable on touch.
 *
 * The `title` sits on a wrapper rather than on `Badge`, which takes a fixed prop
 * list and spreads nothing. Giving `Badge` the same `...rest` treatment `Panel`
 * already has would let this collapse to one element; that is a change to a
 * shared primitive and belongs in its own pass, not smuggled in here.
 */
export function XeroStatusBadge({ link, className }: XeroStatusBadgeProps) {
  return (
    <span className="inline-flex" title={statusDetail(link.status)}>
      <Badge tone={statusTone(link.status)} dot className={className}>
        {statusLabel(link.status)}
      </Badge>
    </span>
  );
}
